import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ViewChild,
  ViewChildren,
  ElementRef,
  AfterViewInit
} from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { UntypedFormGroup } from '@angular/forms';
import { Location } from '@angular/common';

import { FilesPluginService } from './files-plugin.service';
import { BaseModalPageComponent } from '../../base-modal.component';

import { ModalComponent } from '../../modal/modal.component';
import { ConfirmComponent } from '../../modal/confirm.component';
import { InformationComponent } from '../../modal/information.component';
import { Inspection } from '../../shared/models';
import { IndexedDbService } from '../../shared/services/indexedDb.service';
import { LogService } from '../../shared/services/log.service';

@Component({
    selector: 'file-upload',
    templateUrl: './file-upload.component.html',
    standalone: false
})
export class FileUploadPageComponent extends BaseModalPageComponent implements OnInit, AfterViewInit {
  inspectionId: string;
  pointId: string;
  pluginId: string;
  loaded = false;
  files: any[] = [];
  takedPhotos: any[] = [];
  public plugin: any;
  public inspection: Inspection;
  public assets = [];
  editForm: UntypedFormGroup;
  errorMsg = '';
  public processing = false;
  private mediaStreamTrack: any;
  public isTorchPossible: boolean;
  public isFlashOn: boolean;
  public cameras: any;
  public currentCamera: any;
  public isFrontCamera: boolean;
  public isCameraOn = false;
  public assetUrls: any = {};
  @ViewChildren('image') images: ElementRef[];
  @ViewChild('menuModal', { static: true }) menuModal: ModalComponent;
  @ViewChild('deleteConfirm', { static: true }) deleteConfirm: ConfirmComponent;
  @ViewChild('errorMessage', { static: true }) errorMessage: InformationComponent;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private service: FilesPluginService,
    location: Location,
    private cdf: ChangeDetectorRef,
    private logService: LogService,
    private indexedDb: IndexedDbService
  ) {
    super(location);
    this.editForm = new UntypedFormGroup({});
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (id == null) this.router.navigate([this.inspectionsRoute]);

      this.inspectionId = id;

      const pointId = params['pointId'];
      if (pointId == null) this.router.navigate([this.inspectionsRoute, this.inspectionId]);

      this.pointId = pointId;

      const pluginId = params['pluginId'];
      if (pluginId == null) this.goToBack();

      this.pluginId = pluginId;

      this.initData();
    });
  }

  addImages() {
    const inputFile = new FormData();

    for (let i = 0; i < this.files.length; i++) {
      inputFile.append('files[' + i + ']', this.files[i], this.files[i].name);
    }

    for (let i = 0; i < this.takedPhotos.length; i++) {
      inputFile.append(`images`, this.takedPhotos[i]);
    }

    this.processing = true;
    this.service.addFiles(this.inspectionId, this.pointId, this.pluginId, inputFile).subscribe(
      (data) => {
        this.processing = false;
        if (this.inspection.isOffline) {
          data.forEach((a) => {
            this.indexedDb.getImageUrl(`${a}`).subscribe((url) => {
              this.assetUrls[a] = url;
            });
          });
        }
        this.assets.unshift(...data.map((a) => ({ id: a })));
      },
      (error) => {
        this.processing = false;
        window.alert('An error prevented the file upload. Please retry later or contact support for assistance.');
        const data = {
          inspectionId: this.inspectionId,
          pointId: this.pointId,
          pluginId: this.pluginId
        };
        this.logService.logError({ source: 'file-upload.component', action: 'addImages', error, data });
      }
    );
  }

  save() {
    this.service.save(this.inspectionId);
    this.stopStream(this.stream);
    this.goToBack();
  }

  cancel() {
    this.stopStream(this.stream);
    this.service.cancel(this.inspectionId, this.pointId, this.pluginId).subscribe((data) => this.goToBack());
  }

  ngOnInit() {
    this.initRoute();
    this.editForm = new UntypedFormGroup({});
    this.initDevices();
  }

  initData() {
    this.service.getPlugin(this.inspectionId, this.pluginId).subscribe((data) => {
      this.plugin = data;
      this.inspection = data.inspection;
      const assets = this.service.getNewAssets();
      if (this.inspection.isOffline) {
        assets.forEach((a) => {
          this.indexedDb.getImageUrl(a).subscribe((url) => {
            this.assetUrls[a] = url;
            this.assets.push({ id: a, comment: this.plugin.config.Comments ? this.plugin.config.Comments[a] : '' });
          });
        });
      } else {
        assets.forEach((a) => {
          this.assets.push({ id: a, comment: this.plugin.config.Comments ? this.plugin.config.Comments[a] : '' });
        });
      }
    });

    const doc: any = document;
    doc.onpaste = (event) => {
      const items = (event.clipboardData || event.originalEvent.clipboardData).items;
      const pattern = /^image\/(png|jpg|jpeg)$/;
      for (const index in items) {
        const item = items[index];
        if (item.type && item.type.match(pattern)) {
          this.files.push(item.getAsFile());
        }
      }
      if (this.files.length > 0) {
        this.addImages();
        this.files = [];
      }
    };
  }

  ngAfterViewInit() {
    this.service.getEditedImagesIds().subscribe((ids) => {
      ids.forEach((id) => {
        const img = this.images.find((i) => i.nativeElement.src.includes(id));
        if (img) {
          img.nativeElement.src = `${img.nativeElement.src}?v=${Date.now()}`;
        }
      });
    });
  }

  initDevices() {
    if (!navigator.mediaDevices) return;
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      this.cameras = devices.filter((device) => device.kind === 'videoinput');
      if (this.cameras.length > 1) {
        this.currentCamera = this.cameras[0];
        this.isFrontCamera = true;
      }
    });
  }

  handleInputChange(e: any) {
    const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      this.files.push(file);
      const pattern = /^image\/(png|jpg|jpeg)$/;
      const reader = new FileReader();

      if (!file.type.match(pattern)) {
        // alert('invalid format');
        this.errorMessage.open();
        return;
      }
    }
    this.addImages();
    this.files = [];
  }

  clearInput(e: any) {
    e.target.value = null;
  }

  public video: any;
  public stream: any;

  deleteAsset(assetId: string) {
    this.service.deleteFile(this.inspectionId, this.pointId, this.pluginId, assetId).subscribe((data) => {
      this.goToBack();
    });
  }

  stopStream(stream: any) {
    if (stream) {
      stream.getVideoTracks().forEach((track: any) => {
        track.stop();
      });
    }
  }

  // take photo from webcam
  public hasGetUserMedia() {
    const n = <any>navigator;
    return !!(n.getUserMedia || n.webkitGetUserMedia || n.mozGetUserMedia || n.msGetUserMedia);
  }

  // to define
  public width = 350;
  public height = 0;
  public streaming = false;

  public turnOnCamera() {
    this.isCameraOn = true;
    const canvas: any = document.querySelector('#canvas');

    if (this.hasGetUserMedia()) {
      const n = <any>navigator;
      const getUserMedia = (n.getUserMedia || n.webkitGetUserMedia || n.mozGetUserMedia || n.msGetUserMedia).bind(n);

      if (getUserMedia) {
        let constraint = {};
        if (this.currentCamera) {
          constraint = {
            video: {
              deviceId: this.currentCamera.deviceId
            },
            audio: false
          };
        } else {
          constraint = {
            video: true,
            audio: false
          };
        }
        getUserMedia(
          constraint,
          (stream) => {
            this.stream = stream;
            this.video.srcObject = stream;
            this.video.play();
            this.mediaStreamTrack = stream.getVideoTracks()[0];
            setTimeout(() => {
              const capabilities = this.mediaStreamTrack.getCapabilities();
              this.isTorchPossible = capabilities.torch;
              this.cdf.detectChanges();
            }, 600);
          },
          (err) => {
            this.errorMsg = 'An error occured!';
            // console.log("An error occured! " + err);
          }
        );
      } else {
        this.errorMsg = 'no support for media';
        // alert("no support for media");
      }

      const self = this;
      self.video = document.querySelector('#video');

      self.video.addEventListener(
        'canplay',
        (ev: any) => {
          if (!self.streaming) {
            self.height = self.video.videoHeight / (self.video.videoWidth / self.width);

            self.video.setAttribute('width', self.width);
            self.video.setAttribute('height', self.height);
            canvas.setAttribute('width', self.width);
            canvas.setAttribute('height', self.height);
            self.streaming = true;
          }
        },
        false
      );
    }

    this.cdf.detectChanges();
  }

  public takePicture() {
    const self = this;
    const canvas: any = document.querySelector('#canvas');

    if (self.width && self.height) {
      if (this.isTorchPossible && this.isFlashOn) {
        this.mediaStreamTrack.applyConstraints({ advanced: [{ torch: true }] });
        setTimeout(() => {
          this.drawPicture(canvas);
          this.mediaStreamTrack.stop();
          this.turnOnCamera();
        }, 300);
      } else {
        this.drawPicture(canvas);
      }
    }
  }

  private drawPicture(canvas: any) {
    const self = this;
    const context = canvas.getContext('2d');
    canvas.width = self.width;
    canvas.height = self.height;
    context.drawImage(this.video, 0, 0, self.width, self.height);
    const data = canvas.toDataURL('image/png');
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.restore();
    self.takedPhotos.push(data);
    this.addImages();
    self.takedPhotos = [];
    this.cdf.detectChanges();
  }

  public changeFlash() {
    if (this.isTorchPossible) {
      this.isFlashOn = !this.isFlashOn;
    }
  }

  public switchCamera() {
    if (this.currentCamera) {
      if (this.isFrontCamera) {
        this.currentCamera = this.cameras[1];
        this.isFrontCamera = false;
      } else {
        this.currentCamera = this.cameras[0];
        this.isFrontCamera = true;
      }

      this.mediaStreamTrack.stop();
      this.turnOnCamera();
    }
  }

  removeItem(asset: any) {
    this.service.deleteFile(this.inspectionId, this.pointId, this.pluginId, asset.id).subscribe((data) => {
      this.assets = this.assets.filter((a) => a.id !== asset.id);
    });
  }

  edit(asset: any) {
    this.stopStream(this.stream);
    this.router.navigate([
      this.inspectionsRoute,
      this.inspectionId,
      'points',
      this.plugin.pointId ? this.plugin.pointId : -1,
      'plugins',
      this.plugin.id,
      'file',
      asset.id,
      'edit'
    ]);
  }

  download(asset: any) {
    this.service.downloadImage(this.inspectionId, asset.id);
  }

  public getImageUrl(assetId: string) {
    if (this.inspection.isOffline) {
      return this.assetUrls[assetId];
    } else {
      return `/api/assets/${assetId}/content`;
    }
  }

  closeCamera() {
    this.isCameraOn = false;
    this.streaming = false;
    this.stopStream(this.stream);
  }

  public selectMenu(item: any) {
    switch (item) {
      case 1:
        this.openDeleteConfirm();
        break;
    }
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  deletePlugin(e: any) {
    this.service
      .deleteFilesPlugin(this.inspectionId, this.pointId, this.pluginId, this.plugin.config.Assets)
      .subscribe((success: boolean) => {
        if (success) {
          this.goToBack();
        }
      });
  }
}
